/**
 * Video + Subtitles ZIP Strategy
 *
 * Creates ZIP archive with video and all subtitle files.
 * Implements IZipContentStrategy for extensibility.
 *
 * Subtitle files are packaged as SubRip (.srt) — YouTube's caption uploader
 * rejects ASS/SSA, so we must deliver SRT to the user. The backend always
 * generates SRT siblings next to the ASS files used by the FFmpeg pipeline.
 */

import type { IZipContentStrategy, ZipFileEntry } from '../types';
import type { GenerationResponse } from '@/types';

/**
 * Strategy for creating ZIP with video and subtitle files.
 * Useful for YouTube uploads where separate subtitle files are needed.
 */
export class VideoSubtitlesStrategy implements IZipContentStrategy {
  readonly type = 'video_subtitles';

  /**
   * Build file entries from generation response.
   *
   * @param sourceData - GenerationResponse from video generation
   * @returns Array of file entries for ZIP
   */
  buildFileEntries(sourceData: unknown): ZipFileEntry[] {
    const response = sourceData as GenerationResponse;
    const entries: ZipFileEntry[] = [];

    // Add video file
    if (response.video_url) {
      const videoFilename =
        response.filename || `video_${response.generation_id || 'download'}.mp4`;
      entries.push({
        url: response.video_url,
        filename: videoFilename,
        metadata: {
          type: 'video',
          duration: response.duration,
          resolution: response.resolution,
        },
      });
    }

    // Prefer the multilingual SRT map. ASS is the FFmpeg-internal format and
    // is rejected by YouTube — the backend always writes SRT siblings, so we
    // only fall through to single-file paths if the map is missing.
    if (
      response.subtitle_files_srt &&
      typeof response.subtitle_files_srt === 'object' &&
      Object.keys(response.subtitle_files_srt).length > 0
    ) {
      for (const [langCode, url] of Object.entries(response.subtitle_files_srt)) {
        if (url) {
          entries.push({
            url: url as string,
            filename: `subtitles_${langCode}.srt`,
            folder: 'subtitles',
            metadata: {
              type: 'subtitle',
              language: langCode,
              format: 'srt',
            },
          });
        }
      }
    } else if (response.subtitle_url_srt) {
      entries.push({
        url: response.subtitle_url_srt,
        filename: 'subtitles.srt',
        folder: 'subtitles',
        metadata: {
          type: 'subtitle',
          format: 'srt',
        },
      });
    }

    return entries;
  }

  /**
   * Get archive name based on video subject/topic.
   */
  getArchiveName(sourceData: unknown): string {
    const response = sourceData as GenerationResponse;

    // Try to extract a clean name from filename or generation_id
    if (response.filename) {
      // Remove .mp4 extension and use as base name
      const baseName = response.filename.replace(/\.mp4$/i, '');
      return `${baseName}_package`;
    }

    if (response.generation_id) {
      return `video_${response.generation_id}_package`;
    }

    return `video_package_${Date.now()}`;
  }

  /**
   * Generate manifest data for the ZIP.
   */
  getManifestData(sourceData: unknown): Record<string, unknown> {
    const response = sourceData as GenerationResponse;

    return {
      type: 'video_package',
      video: {
        filename: response.filename,
        duration: response.duration,
        resolution: response.resolution,
        fileSize: response.file_size,
      },
      subtitles: {
        available: response.has_subtitles || false,
        languages: response.available_languages || [],
      },
      generatedAt: new Date().toISOString(),
      generationId: response.generation_id,
    };
  }
}

// Export singleton for convenience
export const videoSubtitlesStrategy = new VideoSubtitlesStrategy();
